import React from "react";
import EmailRegistrationModal from "./components/forms/EmailRegistrationModal";
import useEmailRegistration from "./hooks/useEmailRegistration";

export default function About() {
  const { showRegistrationModal, openRegistrationModal, closeRegistrationModal } = useEmailRegistration();
  return (
    <div className="min-h-screen bg-white">

      {/* -- Hero Section -- */}
      <section className="relative bg-[#FFF5EB]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(45deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 140, 0, 0.2) 100%), url('/asset/About/hero backgrounf.png')`,
            opacity: 1,
          }}
        />
        <div className="max-w-6xl mx-auto px-6 py-12 relative z-10">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2">
              <img
                src="/asset/About/hero.png"
                alt="Kitchen Staff"
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </div>
            <div className="md:w-1/2">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-[#2B3674]">
                We Are Redefining Hiring.
                <br />
                <span className="text-[#FF8C00]">Connecting Talent Faster</span>
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* -- Our Story Section -- */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-2xl md:text-3xl font-bold mb-4">Our Story</h2>
        <p className="text-gray-700 mb-6 leading-relaxed">
          Hiree was born out of a real problem—restaurants and hospitality businesses constantly
          struggle to find reliable employees, and job seekers often face challenges landing the right
          opportunities quickly. The hospitality industry is fast-paced, and traditional hiring methods
          can be slow and inefficient. We set out to change that by creating a smart, user-friendly
          platform that prioritizes efficiency, transparency, and trust.
        </p>
        <p className="text-gray-700 mb-6 leading-relaxed">
          Our team is made up of industry experts, technology innovators, and passionate problem solvers
          who understand the needs of both employers and job seekers. Since our launch, we have helped
          countless businesses fill their staffing gaps and provided workers with meaningful employment
          opportunities.
        </p>
        <button 
          onClick={openRegistrationModal}
          className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90">
          Join in
        </button>
      </section>

      {/* -- What Do We Do Section -- */}
      <section className="bg-[#FFF5EB] py-16 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-8">
          {
            <div className="md:w-1/2">
              <img
                src="/asset/About/what do we do.png"
                alt="What do we do illustration"
                className="w-full h-auto"
              />
            </div>
          }

          <div className="md:w-1/2">
            <h2 className="text-2xl md:text-3xl font-bold mb-4 text-center">What do we do?</h2>
            <p className="text-gray-700 mb-8 leading-relaxed text-center">
              We bridge the gap between job seekers looking for flexible, quality jobs and employers in
              need of dependable staff. Our platform offers a range of features designed to make hiring
              and job searching effortless:
            </p>
            <div className="text-center">
              <ul className="inline-block text-left space-y-4 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong className="text-[#2B3674]">Instant Applications</strong> – Apply for jobs with one click and start working fast.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong className="text-[#2B3674]">Verified References</strong> – Employers can review work history and make informed hiring decisions.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong className="text-[#2B3674]">Employer & Job Seeker Reviews</strong> – Build trust and transparency within the community.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong className="text-[#2B3674]">Job Matching Algorithms</strong> – Smart recommendations help candidates find the best-suited roles.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong className="text-[#2B3674]">Interactive Map-Based Search</strong> – Easily locate jobs near you based on preferences and availability.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong className="text-[#2B3674]">Subscription Plans</strong> – Affordable options for both job seekers and employers to maximize hiring efficiency.
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* -- Our Commitment Section -- */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto text-center">

          <h2 className="text-2xl md:text-3xl font-bold mb-8">
            Our Commitment to the Hospitality Industry
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow text-left">
              <div className="flex items-center justify-center mb-6">
                <img 
                  src="/asset/About/Man and woman make a deal over video call.png" 
                  alt="Bridging Gaps" 
                  className="w-32 h-32 object-contain" 
                />
              </div>
              <h3 className="font-bold text-xl mb-4">Bridging Staffing Gaps</h3>
              <p className="text-gray-700">
                Hospitality businesses face high turnover rates and last-minute staffing challenges.
                We help businesses fill roles quickly with qualified, verified candidates.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow text-left">
              <div className="flex items-center justify-center mb-6">
                <img 
                  src="/asset/About/Woman pointing to a contract.png" 
                  alt="Empowering" 
                  className="w-32 h-32 object-contain" 
                /> 
              </div>
              <h3 className="font-bold text-xl mb-4">Empowering Job Seekers</h3>
              <p className="text-gray-700">
                Many hospitality workers seek flexible schedules and reliable employers. Our platform
                makes it easy to find jobs that match their needs, ensuring fair pay and job security.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow text-left">
              <div className="flex items-center justify-center mb-6">
                <img 
                  src="/asset/About/Group of people holding hands together.png" 
                  alt="Trusted Network" 
                  className="w-32 h-32 object-contain" 
                />
              </div>
              <h3 className="font-bold text-xl mb-4">Building a Trusted Network</h3>
              <p className="text-gray-700">
                Verified references and transparent employer-employee reviews build a trustworthy
                hiring system. Employers can confidently hire, knowing a candidate's experience
                is legitimately verified.
              </p>
            </div>
          </div>

          <div className="mt-10">
            <h3 className="text-2xl font-bold mb-2">Find your next job or employee now!</h3>
            <button 
              onClick={openRegistrationModal}
              className="px-6 py-3 bg-[#FF8C00] text-white rounded-lg hover:bg-[#FF8C00]/90">
              Join in
            </button>
          </div>
        </div>
      </section>
      {/* Email Registration Modal */}
      <EmailRegistrationModal 
        show={showRegistrationModal} 
        onClose={closeRegistrationModal} 
      />
    </div>
  );
}
